import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { boxAPI, inventoryAPI } from '../utils/apiClient';
import LoadingSpinner from '../components/LoadingSpinner';
import '../styles/DashboardPage.css';

const DashboardPage = () => {
  const { user, updateUserBalance } = useAuth();
  const [boxes, setBoxes] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [boxesRes, inventoryRes, statsRes] = await Promise.all([
        boxAPI.getAll(),
        inventoryAPI.getInventory({ limit: 5 }),
        inventoryAPI.getStats()
      ]);

      // Normalize boxes
      const boxesData = Array.isArray(boxesRes.data)
        ? boxesRes.data
        : (boxesRes.data?.boxes || []);
      setBoxes(boxesData);

      // Normalize inventory to shape { Item: {...} }
      const invRaw = inventoryRes.data?.inventory || [];
      const invNormalized = invRaw.map((it) => (it?.Item ? it : { id: it.id, Item: it }));
      setInventory(invNormalized);

      // Normalize stats
      const s = statsRes.data || {};
      setStats({
        totalValue: typeof s.totalValue === 'number' ? s.totalValue.toFixed(2) : (s.totalValue || '0.00'),
        totalItems: s.boxesOpened ?? s.totalItems ?? invNormalized.length,
        unsoldItems: s.unsoldItems ?? invNormalized.length,
        rarityDistribution: s.rarityDistribution || {}
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error?.message || error);
      // MVP fallback so Dashboard still renders without API
      const fallbackBoxes = [
        {
          id: 1,
          name: 'Starter Mystery Box',
          price: 10.0,
          description: 'Perfect for beginners! Contains 3-5 jewelry items worth $20-30.',
          imageUrl: '/images/starter-box.jpg',
          category: 'Beginner',
          openedBoxes: 42,
          items: new Array(4).fill(null)
        },
        {
          id: 2,
          name: 'Premium Mystery Box',
          price: 25.0,
          description: 'High-quality jewelry pieces worth $50-75.',
          imageUrl: '/images/premium-box.jpg',
          category: 'Premium',
          openedBoxes: 89,
          items: new Array(6).fill(null)
        },
        {
          id: 3,
          name: 'Luxury Mystery Box',
          price: 50.0,
          description: 'Exclusive luxury jewelry worth $100-150.',
          imageUrl: '/images/luxury-box.jpg',
          category: 'Luxury',
          openedBoxes: 156,
          items: new Array(9).fill(null)
        }
      ];
      setBoxes(fallbackBoxes);
      setInventory([]);
      setStats({ totalValue: '0.00', totalItems: 0, unsoldItems: 0, rarityDistribution: {} });
    } finally {
      setLoading(false);
    }
  };

  const getRarityColor = (rarity) => {
    const colors = {
      common: '#8B9DC3',
      uncommon: '#5CB85C',
      rare: '#5BC0DE',
      epic: '#9B59B6',
      legendary: '#F39C12'
    };
    return colors[rarity] || colors.common;
  };

  if (loading) {
    return <LoadingSpinner message="Loading your dashboard..." />;
  }

  return (
    <div className="dashboard-page">
      <div className="container">
        <div className="dashboard-header">
          <h1>Welcome back, {user?.email}!</h1>
          <p>Ready to open some mystery boxes?</p>
        </div>

        <div className="dashboard-stats">
          <div className="stat-card balance-card">
            <div className="stat-icon">💰</div>
            <div className="stat-content">
              <h3>Account Balance</h3>
              <div className="stat-value">${parseFloat(user?.balance || 0).toFixed(2)}</div>
              <Link to="/wallet" className="stat-action">Add Funds</Link>
            </div>
          </div>

          <div className="stat-card inventory-card">
            <div className="stat-icon">🎒</div>
            <div className="stat-content">
              <h3>Items Owned</h3>
              <div className="stat-value">{stats?.unsoldItems || 0}</div>
              <Link to="/inventory" className="stat-action">View Inventory</Link>
            </div>
          </div>

          <div className="stat-card value-card">
            <div className="stat-icon">💎</div>
            <div className="stat-content">
              <h3>Collection Value</h3>
              <div className="stat-value">${stats?.totalValue || '0.00'}</div>
              <span className="stat-action">Total Worth</span>
            </div>
          </div>

          <div className="stat-card boxes-card">
            <div className="stat-icon">📦</div>
            <div className="stat-content">
              <h3>Boxes Opened</h3>
              <div className="stat-value">{stats?.totalItems || 0}</div>
              <span className="stat-action">Lifetime</span>
            </div>
          </div>
        </div>

        <div className="dashboard-sections">
          <div className="section featured-boxes">
            <div className="section-header">
              <h2>Featured Mystery Boxes</h2>
              <Link to="/" className="view-all-link">View All</Link>
            </div>
            
            <div className="boxes-grid">
              {(boxes?.slice ? boxes.slice(0, 3) : []).map((box) => (
                <Link key={box.id} to={`/box/${box.id}`} className="box-card">
                  <div className="box-image-container">
                    {box.imageUrl ? (
                      <img src={box.imageUrl} alt={box.name} className="box-image" />
                    ) : (
                      <div className="box-placeholder">
                        <span className="box-emoji">🎁</span>
                      </div>
                    )}
                    <div className="box-category-badge">{box.category}</div>
                  </div>
                  
                  <div className="box-info">
                    <h3 className="box-name">{box.name}</h3>
                    <p className="box-description">{box.description}</p>
                    <div className="box-price">
                      <span className="price-value">${parseFloat(box.price).toFixed(2)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="section recent-items">
            <div className="section-header">
              <h2>Recent Items</h2>
              <Link to="/inventory" className="view-all-link">View Inventory</Link>
            </div>

            {inventory.length === 0 ? (
              <div className="empty-state">
                <p>No items yet. Open your first mystery box!</p>
                <Link to="/" className="cta-btn">Browse Boxes</Link>
              </div>
            ) : (
              <div className="items-grid">
                {inventory.map((inventoryItem) => (
                  <div key={inventoryItem.id} className="item-card">
                    <div 
                      className="item-rarity-border"
                      style={{ borderColor: getRarityColor(inventoryItem.Item.rarity) }}
                    >
                      {inventoryItem.Item.imageUrl ? (
                        <img 
                          src={inventoryItem.Item.imageUrl} 
                          alt={inventoryItem.Item.name}
                          className="item-image"
                        />
                      ) : (
                        <div className="item-placeholder">
                          <span className="item-emoji">💎</span>
                        </div>
                      )}
                    </div>
                    <div className="item-info">
                      <h4 className="item-name">{inventoryItem.Item.name}</h4>
                      <span 
                        className="item-rarity"
                        style={{ color: getRarityColor(inventoryItem.Item.rarity) }}
                      >
                        {inventoryItem.Item.rarity}
                      </span>
                      <span className="item-value">
                        ${parseFloat(inventoryItem.Item.value).toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {stats?.rarityDistribution && (
          <div className="section collection-stats">
            <h2>Collection Overview</h2>
            <div className="rarity-distribution">
              {Object.entries(stats.rarityDistribution).map(([rarity, count]) => (
                <div key={rarity} className="rarity-stat">
                  <div 
                    className="rarity-color"
                    style={{ backgroundColor: getRarityColor(rarity) }}
                  ></div>
                  <span className="rarity-name">{rarity}</span>
                  <span className="rarity-count">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
